/**
 * Diversification Matrix API Service
 * Handles data fetching and calculations for the strategic diversification analysis
 */

import { API_BASE_URL as BASE_URL } from './apiConfig';

// Geopolitical classification mapping
// const GEOPOLITICAL_GROUPS = {
//   'BRICS': {
//     countries: ['BRA', 'RUS', 'IND', 'CHN', 'ZAF'],
//     label: 'BRICS',
//     color: '#ef4444' // Red
//   },
//   'G20': {
//     countries: ['ARG', 'AUS', 'BRA', 'CAN', 'CHN', 'FRA', 'DEU', 'IND', 'IDN', 'ITA', 'JPN', 'MEX', 'RUS', 'SAU', 'ZAF', 'KOR', 'TUR', 'GBR', 'USA'],
//     label: 'G20',
//     color: '#3b82f6' // Blue
//   },
//   'QUAD': {
//     countries: ['USA', 'JPN', 'AUS', 'IND'],
//     label: 'QUAD',
//     color: '#10b981' // Green
//   },
//   'SCO': {
//     countries: ['CHN', 'IND', 'IRN', 'KAZ', 'KGZ', 'PAK', 'RUS', 'TJK', 'UZB'],
//     label: 'SCO',
//     color: '#8b5cf6' // Purple
//   },
//   'G4': {
//     countries: ['BRA', 'DEU', 'IND', 'JPN'],
//     label: 'G4',
//     color: '#06b6d4' // Cyan
//   },
//   'I2U2': {
//     countries: ['IND', 'ISR', 'ARE', 'USA'],
//     label: 'I2U2',
//     color: '#f59e0b' // Amber
//   },
//   'Neutral': {
//     countries: ['NOR', 'CHE', 'SWE', 'FIN'],
//     label: 'Neutral',
//     color: '#6b7280' // Gray
//   }
// };

// Simplified geopolitical classification mapping - only BRICS, G20, QUAD
const GEOPOLITICAL_GROUPS = {
  'BRICS': {
    countries: ['BRA', 'RUS', 'IND', 'CHN', 'ZAF'],
    label: 'BRICS',
    color: '#ef4444' // Red
  },
  'G20': {
    countries: ['ARG', 'AUS', 'BRA', 'CAN', 'CHN', 'FRA', 'DEU', 'IND', 'IDN', 'ITA', 'JPN', 'MEX', 'RUS', 'SAU', 'ZAF', 'KOR', 'TUR', 'GBR', 'USA'],
    label: 'G20',
    color: '#3b82f6' // Blue
  },
  'QUAD': {
    countries: ['USA', 'JPN', 'AUS', 'IND'],
    label: 'QUAD',
    color: '#10b981' // Green
  }
};

class DiversificationMatrixService {
  
  /**
   * Get comprehensive diversification matrix data for a mineral
   */
  async getMatrixData(mineral = 'Lithium') {
    try {
      const response = await fetch(`${BASE_URL}/api/diversification-matrix/${encodeURIComponent(mineral)}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch diversification matrix data: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching diversification matrix data:', error);
      throw error;
    }
  }

  /**
   * Fetch ECI ranking data for a specific mineral
   */
  async fetchECIData(mineral) {
    try {
      const response = await fetch(`${BASE_URL}/api/eci-ranking?mineral=${encodeURIComponent(mineral)}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch ECI data: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching ECI data:', error);
      throw error;
    }
  }

  /**
   * Fetch production and reserves data
   */
  async fetchReservesData(mineral) {
    try {
      const response = await fetch(`${BASE_URL}/api/production-reserves?mineral=${encodeURIComponent(mineral)}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch reserves data: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching reserves data:', error);
      throw error;
    }
  }

  /**
   * Fetch partner risk mapping data
   */
  async fetchRiskData() {
    try {
      const response = await fetch(`${BASE_URL}/api/partner-risk-mapping`);
      if (!response.ok) {
        throw new Error(`Failed to fetch risk data: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching risk data:', error);
      throw error;
    }
  }

  /**
   * Fetch India's import dependency data
   */
  async fetchImportData(mineral) {
    try {
      const response = await fetch(`${BASE_URL}/api/india-dependency?mineral=${encodeURIComponent(mineral)}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch import data: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching import data:', error);
      throw error;
    }
  }

  /**
   * Calculate the opportunity matrix combining all data sources
   */
  calculateOpportunityMatrix(eciData, reservesData, riskData, importData) {
    // Calculate global totals for percentage calculations
    const globalReserves = reservesData.reduce((sum, country) => 
      sum + (country.reserve_2024 || 0), 0);
    const globalProduction = reservesData.reduce((sum, country) => 
      sum + (country.prod_2023 || 0), 0);

    return reservesData
      .filter(country => country.reserve_2024 && country.reserve_2024 > 0)
      .map(country => {
        // Find corresponding data from other sources
        const eciInfo = eciData.find(e => 
          e.country_code === country.country_code || 
          e.country?.toLowerCase() === country.country?.toLowerCase()
        );
        
        const riskInfo = riskData.find(r => 
          r.partner_country_code === country.country_code ||
          r.partner_country_name?.toLowerCase() === country.country?.toLowerCase()
        );
        
        const importInfo = importData.find(i => 
          i.country_code === country.country_code ||
          i.source_country?.toLowerCase() === country.country?.toLowerCase()
        );

        // Calculate key metrics
        const reservesPercentage = (country.reserve_2024 / globalReserves) * 100;
        const productionPercentage = country.prod_2023 ? (country.prod_2023 / globalProduction) * 100 : 0;
        const importSharePercentage = importInfo?.india_import_share_percentage || 0;
        const untappedOpportunity = reservesPercentage - importSharePercentage;
        
        // Calculate supply potential (reserves % × reserve-to-production ratio)
        const reserveToProductionRatio = country.prod_2023 && country.prod_2023 > 0 
          ? country.reserve_2024 / country.prod_2023 
          : 0;
        const supplyPotential = reservesPercentage * Math.min(reserveToProductionRatio / 50, 2); // Normalize ratio

        // Get geopolitical classification
        const geopoliticalGroup = this.classifyGeopoliticalGroup(country.country_code);

        return {
          country: country.country,
          iso3: country.country_code,
          eci: eciInfo?.eci || 0,
          pri: riskInfo?.final_risk_score || 3.0, // Default to medium risk
          reservesPercentage,
          productionPercentage,
          importSharePercentage,
          untappedOpportunity,
          supplyPotential,
          reserveToProductionRatio,
          yearsToDepletion: reserveToProductionRatio,
          geopoliticalGroup: geopoliticalGroup.key,
          geopoliticalLabel: geopoliticalGroup.label,
          geopoliticalColor: geopoliticalGroup.color,
          
          // Raw data for reference
          rawReserves: country.reserve_2024,
          rawProduction: country.prod_2023,
          
          // Risk classification
          riskLevel: this.classifyRiskLevel(riskInfo?.final_risk_score || 3.0),
          opportunityLevel: this.classifyOpportunityLevel(untappedOpportunity)
        };
      })
      .sort((a, b) => b.reservesPercentage - a.reservesPercentage); // Sort by reserves percentage
  }

  /**
   * Calculate geopolitical dependency analysis
   */
  calculateGeopoliticalAnalysis(matrixData) {
    const totalReserves = matrixData.reduce((sum, country) => sum + country.reservesPercentage, 0);
    const totalImports = matrixData.reduce((sum, country) => sum + country.importSharePercentage, 0);

    const groupAnalysis = {};
    
    // Initialize groups
    Object.keys(GEOPOLITICAL_GROUPS).forEach(key => {
      groupAnalysis[key] = {
        label: GEOPOLITICAL_GROUPS[key].label,
        color: GEOPOLITICAL_GROUPS[key].color,
        reserveAccess: 0,
        importDependency: 0,
        countries: []
      };
    });

    // Calculate group metrics
    matrixData.forEach(country => {
      const group = groupAnalysis[country.geopoliticalGroup];
      if (group) {
        group.reserveAccess += country.reservesPercentage;
        group.importDependency += country.importSharePercentage;
        group.countries.push(country);
      }
    });

    // Convert to percentages and add derived metrics
    const analysisResult = Object.keys(groupAnalysis).map(key => ({
      id: key,
      label: groupAnalysis[key].label,
      color: groupAnalysis[key].color,
      reserveAccessPercentage: (groupAnalysis[key].reserveAccess / totalReserves) * 100,
      importDependencyPercentage: (groupAnalysis[key].importDependency / totalImports) * 100,
      countries: groupAnalysis[key].countries,
      countryCount: groupAnalysis[key].countries.length
    }));

    return {
      groups: analysisResult,
      metrics: {
        allianceReserveAccess: analysisResult
          .filter(g => ['Quad_Plus', 'Strategic_Partners'].includes(g.id))
          .reduce((sum, g) => sum + g.reserveAccessPercentage, 0),
        rivalProductionControl: analysisResult
          .filter(g => ['BRICS_Plus'].includes(g.id))
          .reduce((sum, g) => sum + g.importDependencyPercentage, 0),
        diversificationIndex: this.calculateDiversificationIndex(matrixData)
      }
    };
  }

  /**
   * Calculate strategic alerts and recommendations
   */
  calculateStrategicAlerts(matrixData, mineral) {
    // Find new opportunities (high reserves, low import share)
    const newOpportunities = matrixData
      .filter(country => 
        country.reservesPercentage > 10 && 
        country.importSharePercentage < 5 &&
        country.untappedOpportunity > 15
      )
      .sort((a, b) => b.untappedOpportunity - a.untappedOpportunity)
      .slice(0, 3);

    // Find depletion risks (low years to depletion for major suppliers)
    const depletionRisks = matrixData
      .filter(country => 
        country.importSharePercentage > 10 && 
        country.yearsToDepletion < 20 && 
        country.yearsToDepletion > 0
      )
      .sort((a, b) => a.yearsToDepletion - b.yearsToDepletion);

    // Find geopolitical concentration risks
    const rivalExposure = matrixData
      .filter(country => country.geopoliticalGroup === 'BRICS_Plus')
      .reduce((sum, country) => sum + country.importSharePercentage, 0);

    return {
      newOpportunities,
      depletionRisks,
      topOpportunity: newOpportunities[0] || null,
      rivalExposure,
      diversificationIndex: this.calculateDiversificationIndex(matrixData),
      allianceDependency: matrixData
        .filter(country => ['Quad_Plus', 'Strategic_Partners'].includes(country.geopoliticalGroup))
        .reduce((sum, country) => sum + country.importSharePercentage, 0),
      recommendations: this.generateRecommendations(matrixData, mineral)
    };
  }

  /**
   * Calculate diversification index (0-1, higher = more diversified)
   */
  calculateDiversificationIndex(matrixData) {
    const importShares = matrixData.map(country => country.importSharePercentage / 100);
    const hhi = importShares.reduce((sum, share) => sum + (share * share), 0);
    return Math.max(0, (1 - hhi) * 1.2); // Normalize and scale
  }

  /**
   * Classify geopolitical group for a country
   * Only classifies into BRICS, G20, QUAD, or Neutral
   */
  classifyGeopoliticalGroup(countryCode) {
    // Check BRICS first
    if (GEOPOLITICAL_GROUPS.BRICS.countries.includes(countryCode)) {
      return {
        key: 'BRICS',
        label: 'BRICS',
        color: '#ef4444'
      };
    }
    
    // Check G20
    if (GEOPOLITICAL_GROUPS.G20.countries.includes(countryCode)) {
      return {
        key: 'G20',
        label: 'G20',
        color: '#3b82f6'
      };
    }
    
    // Check QUAD
    if (GEOPOLITICAL_GROUPS.QUAD.countries.includes(countryCode)) {
      return {
        key: 'QUAD',
        label: 'QUAD',
        color: '#10b981'
      };
    }
    
    // Default to neutral for unclassified countries
    return {
      key: 'Neutral',
      label: 'Neutral',
      color: '#6b7280'
    };
  }

  /**
   * Classify risk level based on PRI score
   */
  classifyRiskLevel(priScore) {
    if (priScore >= 4.0) return 'Low';
    if (priScore >= 3.0) return 'Medium';
    if (priScore >= 2.0) return 'High';
    return 'Critical';
  }

  /**
   * Classify opportunity level
   */
  classifyOpportunityLevel(untappedOpportunity) {
    if (untappedOpportunity >= 20) return 'High';
    if (untappedOpportunity >= 10) return 'Medium';
    if (untappedOpportunity >= 0) return 'Low';
    return 'Over-exposed';
  }

  /**
   * Generate strategic recommendations
   */
  generateRecommendations(matrixData, mineral) {
    const recommendations = [];
    
    // High opportunity countries
    const highOpportunities = matrixData.filter(c => c.untappedOpportunity > 15);
    if (highOpportunities.length > 0) {
      recommendations.push({
        type: 'opportunity',
        title: 'Diversification Opportunities',
        description: `Consider increasing imports from ${highOpportunities[0].country} (${highOpportunities[0].untappedOpportunity.toFixed(1)}% untapped potential)`,
        priority: 'high',
        countries: highOpportunities.slice(0, 3)
      });
    }

    // Geopolitical risk
    const rivalDependency = matrixData
      .filter(c => c.geopoliticalGroup === 'BRICS_Plus')
      .reduce((sum, c) => sum + c.importSharePercentage, 0);
    
    if (rivalDependency > 40) {
      recommendations.push({
        type: 'risk',
        title: 'Geopolitical Concentration Risk',
        description: `High dependency on rival nations (${rivalDependency.toFixed(1)}%). Consider diversifying to allied countries.`,
        priority: 'high'
      });
    }

    return recommendations;
  }

}

// Export singleton instance
export const diversificationMatrixService = new DiversificationMatrixService();
export default diversificationMatrixService;
